import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import RichTextEditor from "../../components/ui/RichTextEditor";
import Select from "../../components/ui/Select";
import Switch from "../../components/ui/Switch";
import {
    createArtistRequest,
    getArtistCategoriesRequest,
    getArtistsRequest,
    updateArtistRequest,
} from "../../services/api/content.api";
import {
    ArtistCategory,
    ArtistItem,
} from "../../services/types/content.types";

interface ArtistFormState {
    nameRu: string;
    nameEn: string;
    nameDe: string;
    nameUa: string;
    descriptionRu: string;
    descriptionEn: string;
    descriptionDe: string;
    descriptionUa: string;
    categoryId: string;
    isActive: boolean;
}

const initialFormState: ArtistFormState = {
    nameRu: "",
    nameEn: "",
    nameDe: "",
    nameUa: "",
    descriptionRu: "",
    descriptionEn: "",
    descriptionDe: "",
    descriptionUa: "",
    categoryId: "",
    isActive: true,
};

const ArtistsPage: React.FC = () => {
    const [items, setItems] = useState<ArtistItem[]>([]);
    const [categories, setCategories] = useState<ArtistCategory[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<ArtistFormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        if (!query) {
            return items;
        }

        return items.filter((item) => {
            return (
                item.name_ru.toLowerCase().includes(query) ||
                item.name_en.toLowerCase().includes(query) ||
                item.name_de.toLowerCase().includes(query) ||
                (item.name_ua ?? "").toLowerCase().includes(query)
            );
        });
    }, [items, searchValue]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const [artistsResponse, categoriesResponse] = await Promise.all([
                getArtistsRequest(),
                getArtistCategoriesRequest(),
            ]);

            setItems(artistsResponse);
            setCategories(categoriesResponse);
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить данные");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, []);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSelect = (item: ArtistItem) => {
        setSelectedId(item.id);
        setFormState({
            nameRu: item.name_ru,
            nameEn: item.name_en,
            nameDe: item.name_de,
            nameUa: item.name_ua ?? "",
            descriptionRu: item.description_ru ?? "",
            descriptionEn: item.description_en ?? "",
            descriptionDe: item.description_de ?? "",
            descriptionUa: item.description_ua ?? "",
            categoryId: item.category_id ?? "",
            isActive: item.is_active,
        });
        setFormError(null);
        setSuccessMessage(null);
    };

    const categoryOptions = categories.map((item) => ({
        value: item.id,
        label: item.name_ru,
    }));

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.nameRu.trim() || !formState.nameEn.trim() || !formState.nameDe.trim()) {
            setFormError("Имя артиста должно быть заполнено на трёх языках.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                name_ru: formState.nameRu.trim(),
                name_en: formState.nameEn.trim(),
                name_de: formState.nameDe.trim(),
                name_ua: formState.nameUa.trim() || null,
                description_ru: formState.descriptionRu.trim() || null,
                description_en: formState.descriptionEn.trim() || null,
                description_de: formState.descriptionDe.trim() || null,
                description_ua: formState.descriptionUa.trim() || null,
                category_id: formState.categoryId || null,
                is_active: formState.isActive,
            };

            let savedItem: ArtistItem;

            if (selectedId) {
                savedItem = await updateArtistRequest(selectedId, payload);
            } else {
                savedItem = await createArtistRequest(payload);
            }

            setItems((prev) => {
                const exists = prev.some((item) => item.id === savedItem.id);

                if (exists) {
                    return prev.map((item) => (item.id === savedItem.id ? savedItem : item));
                }

                return [savedItem, ...prev];
            });

            handleSelect(savedItem);
            setSuccessMessage(selectedId ? "Артист обновлён." : "Артист создан.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Ошибка сохранения артиста");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Артисты"
                description="Каталог артистов с локализациями, категорией и описанием."
                actions={
                    <Button onClick={resetForm}>
                        Новый артист
                    </Button>
                }
            />

            <section className="content-split artist-layout">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список артистов</h3>
                            <p className="card-text">Выберите запись для редактирования или создайте новую.</p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск артиста..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Артисты не найдены</h3>
                            <p className="empty-block-text">Добавьте первую запись в каталог артистов.</p>
                        </div>
                    ) : (
                        <div className="artist-list-grid">
                            {filteredItems.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`artist-list-item artist-list-item-compact ${item.id === selectedId ? "artist-list-item-active" : ""}`}
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className="artist-list-content">
                                        <span className="artist-list-title">{item.name_ru}</span>
                                        <span className="artist-list-subtitle">{item.name_en}</span>
                                    </div>

                                    <span
                                        className={`status-dot ${item.is_active ? "status-dot-active" : "status-dot-inactive"}`}
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">{selectedId ? "Редактирование артиста" : "Новый артист"}</h3>
                            <p className="card-text">
                                Поле slug удалено, а категория теперь отправляется как category_id.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid form-grid-two-columns">
                            <Field label="Имя (RU)" htmlFor="artist-name-ru">
                                <Input
                                    id="artist-name-ru"
                                    value={formState.nameRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameRu: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Имя (EN)" htmlFor="artist-name-en">
                                <Input
                                    id="artist-name-en"
                                    value={formState.nameEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameEn: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Имя (DE)" htmlFor="artist-name-de">
                                <Input
                                    id="artist-name-de"
                                    value={formState.nameDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameDe: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Имя (UA)" htmlFor="artist-name-ua">
                                <Input
                                    id="artist-name-ua"
                                    value={formState.nameUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameUa: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Категория" htmlFor="artist-category">
                                <Select
                                    id="artist-category"
                                    options={categoryOptions}
                                    value={formState.categoryId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, categoryId: event.target.value }))
                                    }
                                    placeholder="Выберите категорию"
                                />
                            </Field>
                        </div>

                        <RichTextEditor
                            label="Описание (RU)"
                            value={formState.descriptionRu}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, descriptionRu: value }))
                            }
                        />

                        <RichTextEditor
                            label="Описание (EN)"
                            value={formState.descriptionEn}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, descriptionEn: value }))
                            }
                        />

                        <RichTextEditor
                            label="Описание (DE)"
                            value={formState.descriptionDe}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, descriptionDe: value }))
                            }
                        />

                        <RichTextEditor
                            label="Описание (UA)"
                            value={formState.descriptionUa}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, descriptionUa: value }))
                            }
                        />

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isActive: checked }))
                                }
                                label={formState.isActive ? "Артист активен" : "Артист выключен"}
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving ? "Сохраняем..." : selectedId ? "Сохранить изменения" : "Создать артиста"}
                            </Button>

                            <Button type="button" variant="secondary" onClick={resetForm}>
                                Очистить форму
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default ArtistsPage;
